import { Link } from "react-router-dom";
import { useCan } from "../../lib/auth";
import { Icon } from "../../components/ui/Icon";

/**
 * One place to make things.
 *
 * Articles, Documents and YouTube used to be three top-level tabs in the
 * header, which put an author's tools in front of everyone who could see them
 * and pushed the reading surfaces along. They are the same activity — editing
 * what the site publishes — so they sit behind one door, and Home is left to be
 * what it should be: what there is to read.
 *
 * The tab is INVISIBLE to anyone with no authoring permission (see
 * `useHeaderTabs`), and each desk here is gated on the same permission the
 * server enforces at its destination, so nothing is offered that would 403.
 *
 * Built from the house vocabulary — `page-eyebrow` / `page-title` for the head,
 * `.veda-card` for the desks — rather than from ad-hoc utility classes, so it
 * inherits the same rule colour, radius, hover lift and easing as every other
 * card on the site instead of approximating them.
 */

interface Desk {
  to: string;
  label: string;
  deva: string;
  blurb: string;
  can: boolean;
}

export default function Editor() {
  const desks: Desk[] = [
    {
      to: "/dashboard/articles",
      label: "Articles",
      deva: "वार्ता",
      blurb:
        "News and long-form writing. Draft against a template, revise with full history, publish when it reads right.",
      can: useCan("articles.create") || useCan("articles.edit_any"),
    },
    {
      to: "/dashboard/documents",
      label: "Documents",
      deva: "ग्रन्थ",
      blurb:
        "The Library’s texts — mantras, sūktas, sādhanās — with their attachments, recordings and tags.",
      can: useCan("documents.create") || useCan("documents.edit_any"),
    },
    {
      to: "/dashboard/chants",
      label: "Chants",
      deva: "मन्त्र",
      blurb:
        "The marked recitation texts — holdings, svaras, anusvāra and visarga, in every script. A Library document points at one; this is where the text itself is written.",
      can: useCan("chants.create") || useCan("chants.edit_any"),
    },
    {
      to: "/dashboard/youtube",
      label: "YouTube",
      deva: "चलचित्र",
      blurb:
        "The channel. Upload, edit metadata, arrange series, and watch the daily quota.",
      can: useCan("youtube.manage"),
    },
  ];
  const mine = desks.filter((d) => d.can);

  return (
    <div>
      <header className="mb-9 max-w-[62ch]">
        <p className="page-eyebrow mb-1.5">Editor</p>
        <h1 className="page-title">
          What you can add to, and <span className="font-display-italic">change</span>.
        </h1>
        <p className="font-grotesk text-[0.82rem] leading-relaxed text-[var(--color-ink-soft)] mt-3">
          Only the desks your role opens are shown. Everything published from
          here appears in the Library and on the public site.
        </p>
      </header>

      {mine.length === 0 ? (
        <div className="veda-empty">
          <p className="font-display text-[1.4rem] leading-[1.2] tracking-tight mb-2">
            Nothing to edit yet.
          </p>
          <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-soft)] max-w-[46ch] mx-auto leading-[1.6]">
            Your role doesn’t include authoring permissions. Ask an
            administrator if you should be writing here.
          </p>
        </div>
      ) : (
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {mine.map((d) => (
            <li key={d.to}>
              <Link to={d.to} className="veda-card group block h-full">
                <span
                  className="veda-card-deva absolute right-4 top-2 select-none !text-[2.4rem] !mb-0 opacity-[0.14] group-hover:opacity-[0.22] transition-opacity"
                  aria-hidden
                >
                  {d.deva}
                </span>
                <h2 className="font-display text-[1.5rem] leading-[1.15] tracking-tight">
                  {d.label}
                </h2>
                <p className="font-grotesk text-[0.78rem] leading-relaxed text-[var(--color-ink-soft)] mt-2.5 pr-6">
                  {d.blurb}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-grotesk text-[0.72rem] uppercase tracking-[0.14em] text-[var(--color-violet)]">
                  Open
                  <Icon name="arrow-right" size="0.95em" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
